import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
  KeyboardAvoidingView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Text from '../../components/common/Text';
import { RootStackParamList } from '../../types/navigation';
import TextField from '../../components/common/TextField';
import Button from '../../components/common/Button';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import EyeIcon from '../../components/common/EyeIcon';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { authAPI } from '../../api/auth';
import { useAuth } from '../../hooks/useAuth';

const setPasswordSchema = z
  .object({
    newPassword: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type SetPasswordForm = z.infer<typeof setPasswordSchema>;

type CallbackState =
  'loading' | 'email_confirmed' | 'set_password' | 'done' | 'error';

const resendEmailSchema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type ResendEmailForm = z.infer<typeof resendEmailSchema>;

const AuthCallbackScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { colors } = useTheme();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const { resendVerification, signOut } = useAuth();
  const [state, setState] = useState<CallbackState>('loading');
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [accessTokenStored, setAccessTokenStored] = useState<string>('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const {
    control: resendControl,
    handleSubmit: handleResendSubmit,
    formState: { errors: resendErrors },
  } = useForm<ResendEmailForm>({
    resolver: zodResolver(resendEmailSchema),
    defaultValues: { email: '' },
  });

  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const formPadding = isMobile ? 24 : isTablet ? 40 : 50;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SetPasswordForm>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  useEffect(() => {
    let cancelled = false;

    const getHashFromUrl = (): string => {
      if (
        Platform.OS === 'web' &&
        typeof window !== 'undefined' &&
        window.location?.hash
      ) {
        return window.location.hash;
      }
      return '';
    };

    const getSearchFromUrl = (): string => {
      if (
        Platform.OS === 'web' &&
        typeof window !== 'undefined' &&
        window.location?.search
      ) {
        return window.location.search;
      }
      return '';
    };

    const run = async () => {
      const hash = getHashFromUrl();
      const search = getSearchFromUrl();

      // Check for Supabase error params (expired/invalid link) in hash or query
      const hashParams = hash
        ? new URLSearchParams(hash.replace(/^#/, ''))
        : null;
      const searchParams = search
        ? new URLSearchParams(search.replace(/^\?/, ''))
        : null;
      const error =
        hashParams?.get('error') ?? searchParams?.get('error') ?? null;
      const errorDescription =
        hashParams?.get('error_description') ??
        searchParams?.get('error_description') ??
        null;

      if (error || errorDescription) {
        if (!cancelled) {
          setState('error');
          setMessage(
            errorDescription?.replace(/\+/g, ' ') ??
              'This link is invalid or has expired. Request a new verification link below.'
          );
        }
        return;
      }

      if (!hash) {
        if (!cancelled) {
          setState('error');
          setMessage(
            'Invalid or expired link. Enter your email below to receive a new verification link.'
          );
        }
        return;
      }

      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const accessToken = params.get('access_token');
      const type = params.get('type') || '';

      if (!accessToken) {
        if (!cancelled) {
          setState('error');
          setMessage(
            'Invalid or expired link. Enter your email below to receive a new verification link.'
          );
        }
        return;
      }

      if (!cancelled) {
        if (type === 'recovery') {
          setState('set_password');
          setAccessTokenStored(accessToken);
        } else {
          setState('email_confirmed');
          setTimeout(async () => {
            await signOut();
            navigation.replace('Login');
          }, 4000);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigation, signOut]);

  const onSetPassword = async (data: SetPasswordForm) => {
    if (!accessTokenStored) {
      setMessage('Session expired. Please request a new reset link.');
      return;
    }
    const response = await authAPI.confirmPasswordReset(
      accessTokenStored,
      data.newPassword
    );
    if (!response.success) {
      setMessage(
        response.error ||
          response.detail ||
          response.message ||
          'Failed to update password.'
      );
      return;
    }
    setState('done');
    setMessage('Password updated. You can now sign in.');
    setTimeout(async () => {
      await signOut();
      navigation.replace('Login');
    }, 2000);
  };

  if (state === 'loading') {
    return (
      <ResponsiveContainer
        style={
          [styles.container, { backgroundColor: colors.authBackground }] as [
            object,
            object,
          ]
        }
      >
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text
            size="lg"
            weight="medium"
            style={{ color: colors.text, marginTop: 24 }}
          >
            Completing verification…
          </Text>
          <Text size="md" style={{ color: colors.secondary, marginTop: 8 }}>
            Please wait a moment
          </Text>
        </View>
      </ResponsiveContainer>
    );
  }

  const onResendVerification = async (data: ResendEmailForm) => {
    setResendSuccess(false);
    setResendLoading(true);
    try {
      await resendVerification(data.email);
      setResendSuccess(true);
      // Navigate to verification email page (Check your email flow)
      navigation.replace('VerifyEmailRequired');
    } catch {
      // Error shown via Alert in useAuth
    } finally {
      setResendLoading(false);
    }
  };

  if (state === 'error') {
    return (
      <ResponsiveContainer
        style={
          [styles.container, { backgroundColor: colors.authBackground }] as [
            object,
            object,
          ]
        }
      >
        <ScrollView
          contentContainerStyle={styles.centeredScroll}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={[
              styles.iconCircle,
              { backgroundColor: colors.errorBackground },
            ]}
          >
            <Ionicons name="alert-circle" size={48} color="#EF4444" />
          </View>
          <Text
            size="xl"
            weight="bold"
            style={{
              color: colors.text,
              marginBottom: 12,
              textAlign: 'center',
            }}
          >
            Link expired or invalid
          </Text>
          <Text
            size="md"
            lineHeight="relaxed"
            style={{
              color: colors.secondary,
              textAlign: 'center',
              marginBottom: 32,
            }}
          >
            {message}
          </Text>

          <View style={styles.resendSection}>
            <Text
              size="lg"
              weight="semibold"
              style={{
                color: colors.text,
                marginBottom: 16,
                textAlign: 'center',
              }}
            >
              Get a new verification link
            </Text>
            <Controller
              control={resendControl}
              name="email"
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  label="Email address"
                  placeholder="Enter the email you signed up with"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={resendErrors.email?.message}
                />
              )}
            />
            {resendSuccess && (
              <View
                style={[
                  styles.successChip,
                  { backgroundColor: palette.lightCyanGreen },
                ]}
              >
                <Ionicons name="checkmark-circle" size={20} color="#059669" />
                <Text
                  size="md"
                  weight="medium"
                  style={{ color: palette.darkCyanLimeGreen, marginLeft: 8 }}
                >
                  New email sent. Check your inbox.
                </Text>
              </View>
            )}
            <Button
              title="Send new verification email"
              onPress={handleResendSubmit(onResendVerification)}
              loading={resendLoading}
              disabled={resendLoading}
              style={{ marginTop: 16 }}
            />
          </View>

          <Button
            title="Back to sign in"
            variant="outline"
            onPress={async () => {
              await signOut();
              navigation.navigate('Login');
            }}
            style={{ marginTop: 28 }}
          />
        </ScrollView>
      </ResponsiveContainer>
    );
  }

  if (state === 'email_confirmed') {
    return (
      <ResponsiveContainer
        style={
          [styles.container, { backgroundColor: colors.authBackground }] as [
            object,
            object,
          ]
        }
      >
        <View style={styles.centered}>
          <View
            style={[
              styles.iconCircle,
              { backgroundColor: palette.lightCyanGreen },
            ]}
          >
            <Ionicons name="checkmark-circle" size={64} color="#059669" />
          </View>
          <Text
            size="2xl"
            weight="bold"
            style={{ color: colors.text, marginTop: 24, textAlign: 'center' }}
          >
            Email verified!
          </Text>
          <Text
            size="lg"
            lineHeight="relaxed"
            style={{
              color: colors.secondary,
              marginTop: 12,
              textAlign: 'center',
            }}
          >
            You&apos;re all set. Sign in to access your account.
          </Text>
          <Button
            title="Sign in now"
            onPress={async () => {
              await signOut();
              navigation.replace('Login');
            }}
            style={{ marginTop: 32, minWidth: 200 }}
          />
          <Text
            size="sm"
            style={{ color: colors.secondary, marginTop: 20, opacity: 0.8 }}
          >
            Redirecting automatically in a few seconds…
          </Text>
        </View>
      </ResponsiveContainer>
    );
  }

  if (state === 'done') {
    return (
      <ResponsiveContainer
        style={
          [styles.container, { backgroundColor: colors.authBackground }] as [
            object,
            object,
          ]
        }
      >
        <View style={styles.centered}>
          <View
            style={[
              styles.iconCircle,
              { backgroundColor: palette.lightCyanGreen },
            ]}
          >
            <Ionicons name="checkmark-circle" size={64} color="#059669" />
          </View>
          <Text
            size="xl"
            weight="bold"
            style={{ color: colors.text, marginTop: 24, textAlign: 'center' }}
          >
            {message}
          </Text>
          <Text
            size="md"
            style={{
              color: colors.secondary,
              marginTop: 12,
              textAlign: 'center',
            }}
          >
            Redirecting to sign in…
          </Text>
          <Button
            title="Sign in now"
            onPress={async () => {
              await signOut();
              navigation.replace('Login');
            }}
            style={{ marginTop: 28, minWidth: 200 }}
          />
        </View>
      </ResponsiveContainer>
    );
  }

  if (state === 'set_password') {
    return (
      <ResponsiveContainer
        style={
          [styles.container, { backgroundColor: colors.authBackground }] as [
            object,
            object,
          ]
        }
      >
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContentContainer}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View
              style={[
                styles.contentContainer,
                !isMobile && styles.webContentContainer,
              ]}
            >
              <View
                style={[
                  styles.formContainer,
                  {
                    backgroundColor: colors.card,
                    shadowColor: colors.border,
                    borderColor: colors.borderLight,
                    paddingHorizontal: formPadding,
                  },
                ]}
              >
                <View style={styles.formHeader}>
                  <View
                    style={[
                      styles.iconCircleSmall,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.borderLight,
                      },
                    ]}
                  >
                    <Ionicons name="key" size={28} color={colors.primary} />
                  </View>
                  <Text
                    size="xl"
                    weight="bold"
                    style={[styles.formTitle, { color: colors.primary }]}
                  >
                    Set new password
                  </Text>
                  <Text
                    size="md"
                    lineHeight="relaxed"
                    style={[styles.formSubtitle, { color: colors.secondary }]}
                  >
                    Choose a secure password. You&apos;ll use it to sign in from
                    now on.
                  </Text>
                </View>
                <View style={styles.form}>
                  <Controller
                    control={control}
                    name="newPassword"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextField
                        label="New password"
                        placeholder="Enter new password"
                        secureTextEntry={!showNew}
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        error={errors.newPassword?.message}
                        rightIcon={<EyeIcon isVisible={showNew} />}
                        onRightIconPress={() => setShowNew(!showNew)}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="confirmPassword"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextField
                        label="Confirm password"
                        placeholder="Confirm new password"
                        secureTextEntry={!showConfirm}
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        error={errors.confirmPassword?.message}
                        rightIcon={<EyeIcon isVisible={showConfirm} />}
                        onRightIconPress={() => setShowConfirm(!showConfirm)}
                      />
                    )}
                  />
                  {message ? (
                    <View
                      style={[
                        styles.errorContainer,
                        { backgroundColor: colors.errorBackground },
                      ]}
                    >
                      <Text
                        size="sm"
                        weight="medium"
                        style={{ color: colors.errorText }}
                      >
                        {message}
                      </Text>
                    </View>
                  ) : null}
                  <Button
                    title="Update password"
                    onPress={handleSubmit(onSetPassword)}
                    style={styles.button}
                  />
                </View>
                <View style={styles.footer}>
                  <Button
                    title="Back to sign in"
                    variant="outline"
                    onPress={async () => {
                      await signOut();
                      navigation.navigate('Login');
                    }}
                  />
                </View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </ResponsiveContainer>
    );
  }

  return null;
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  keyboardAvoidingView: { flex: 1, zIndex: 10 },
  scrollView: { flex: 1 },
  scrollContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    minHeight: Platform.OS === 'web' ? undefined : '100%',
  },
  contentContainer: { maxWidth: 440, width: '100%', alignSelf: 'center' },
  webContentContainer: { maxWidth: 440 },
  formContainer: {
    borderRadius: 16,
    borderWidth: 1,
    paddingVertical: 28,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  formHeader: { alignItems: 'center', marginBottom: 24 },
  formTitle: { marginBottom: 8 },
  formSubtitle: { textAlign: 'center', paddingHorizontal: 8 },
  form: { gap: 16 },
  button: { marginTop: 8 },
  errorContainer: { padding: 12, borderRadius: 8, marginTop: 8 },
  footer: { marginTop: 24, alignItems: 'center' },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  centeredScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  resendSection: {
    width: '100%',
    maxWidth: 360,
  },
  iconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconCircleSmall: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    marginBottom: 16,
  },
  successChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginTop: 16,
  },
});

export default AuthCallbackScreen;
